import type { NormalizedRoundSchedule } from "../../domain/round/types";

export type GeneratorLock = {
  employeeId?: string;
  name?: string;
  group: number;
  kind: "full" | "h1" | "h2";
  role?: "primary" | "secondary";
};

function kind(segment: string): "full" | "h1" | "h2" | null {
  if (segment === "full18" || segment === "extraFull18") return "full";
  if (segment === "holes1to9" || segment === "extra1to9") return "h1";
  if (segment === "holes10to18" || segment === "extra10to18") return "h2";
  return null;
}

/**
 * Turn a current v2 workspace into the exact lock contract understood by the
 * develop-v1 solver. We intentionally lock people/slots, not transport; the
 * next generation may still rebuild cars around the frozen scoring plan.
 */
export function locksFromSchedule(schedule: NormalizedRoundSchedule | null | undefined): GeneratorLock[] {
  if (!schedule) return [];
  const people = new Map(schedule.people.map((person) => [person.personId, person]));
  const groups = new Map(schedule.groups.map((group) => [group.id, group]));
  const locks: GeneratorLock[] = [];

  for (const assignment of schedule.assignments || []) {
    const targetKind = kind(String(assignment.segment || ""));
    const group = groups.get(assignment.groupId);
    const person = people.get(assignment.personId);
    if (!targetKind || !group || !person) continue;
    locks.push({
      employeeId: person.personId,
      name: person.displayName,
      group: Number(group.number || 0),
      kind: targetKind,
      ...(String(assignment.segment || "").startsWith("extra") ? { role: "secondary" as const } : {}),
    });
  }

  const unique = new Map<string, GeneratorLock>();
  for (const lock of locks) {
    const key = `${lock.employeeId || lock.name}|${lock.group}|${lock.kind}|${lock.role || ""}`;
    unique.set(key, lock);
  }
  return [...unique.values()];
}
